import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbLock } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { LinearIcon } from "./linearIcon";
import { type LinearTeamsPayload, parseLinearTeamsPayload } from "./linearTeamsSchema";

interface LinearTeamsRendererProps {
  block: StreamingBlock;
}

export const LinearTeamsRenderer = (props: LinearTeamsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LinearTeamsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLinearTeamsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("linear_teams_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().teams.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <LinearIcon class="size-4 shrink-0 text-primary-text" />
                <span class="truncate text-sm font-medium text-primary-text">{rt.t("linear_teams_card.title")}</span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().teams.length === 1
                    ? rt.t("linear_teams_card.results_one")
                    : rt.t("linear_teams_card.results", { count: String(data().teams.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().teams}>
                  {(team) => (
                    <li class="flex items-center gap-2 px-3 py-2">
                      <span class="shrink-0 font-mono text-xs text-muted-text">{team.key}</span>
                      <span class="truncate text-sm text-primary-text">{team.name}</span>
                      <Show when={team.private}>
                        <span class="ml-auto inline-flex shrink-0 items-center gap-1 text-xs text-muted-text">
                          <TbLock class="size-3.5" aria-hidden="true" />
                          {rt.t("linear_teams_card.private_badge")}
                        </span>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
